import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowUpDown, Info, Clock, ChevronDown, ChevronUp, Mail, CheckSquare, Square, Lock } from 'lucide-react';
import { cryptoApi } from '../utils/api';
import api from '../utils/api';
import { useLanguage } from '../i18n/LanguageContext';
import toast from 'react-hot-toast';
import { CRYPTO_LOGOS, flagUrl } from '../utils/currencies';

const PAYMENT_LOGOS = {
  revolut: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Revolut_logo.svg/200px-Revolut_logo.svg.png',
  societe_generale: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/99/Soci%C3%A9t%C3%A9_G%C3%A9n%C3%A9rale.svg/200px-Soci%C3%A9t%C3%A9_G%C3%A9n%C3%A9rale.svg.png',
  visa: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/200px-Visa_Inc._logo.svg.png',
  mastercard: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/200px-Mastercard-logo.svg.png',
};

const PAYMENT_METHODS = {
  fiat: [
    { id: 'sepa', name: 'Virement SEPA', type: 'transfer', logo: flagUrl('european_union'), color: '#003399' },
    { id: 'revolut', name: 'Revolut', type: 'transfer', logo: PAYMENT_LOGOS.revolut, color: '#0075EB' },
    { id: 'societe_generale', name: 'Société Générale', type: 'bank', logo: PAYMENT_LOGOS.societe_generale, color: '#E60000' },
    { id: 'visa', name: 'Carte Visa', type: 'card', logo: PAYMENT_LOGOS.visa, color: '#1A1F71' },
    { id: 'mastercard', name: 'Mastercard', type: 'card', logo: PAYMENT_LOGOS.mastercard, color: '#EB001B' },
    { id: 'pcs', name: 'PCS Mastercard', type: 'card', color: '#F59E0B' },
    { id: 'transcash', name: 'Transcash', type: 'card', color: '#00B67A' },
    { id: 'cashlib', name: 'Cashlib', type: 'card', color: '#FF4B4B' },
    { id: 'neosurf', name: 'Neosurf', type: 'card', color: '#84CC16' },
  ],
  crypto: [
    { id: 'btc', name: 'Bitcoin', symbol: 'BTC', logo: CRYPTO_LOGOS.BTC },
    { id: 'eth', name: 'Ethereum', symbol: 'ETH', logo: CRYPTO_LOGOS.ETH },
    { id: 'usdt', name: 'Tether (TRC20)', symbol: 'USDT', logo: CRYPTO_LOGOS.USDT },
    { id: 'usdc', name: 'USD Coin', symbol: 'USDC', logo: CRYPTO_LOGOS.USDC },
    { id: 'bnb', name: 'BNB', symbol: 'BNB', logo: CRYPTO_LOGOS.BNB },
    { id: 'sol', name: 'Solana', symbol: 'SOL', logo: CRYPTO_LOGOS.SOL },
    { id: 'xrp', name: 'Ripple', symbol: 'XRP', logo: CRYPTO_LOGOS.XRP },
    { id: 'ltc', name: 'Litecoin', symbol: 'LTC', logo: CRYPTO_LOGOS.LTC },
    { id: 'doge', name: 'Dogecoin', symbol: 'DOGE', logo: CRYPTO_LOGOS.DOGE },
    { id: 'ada', name: 'Cardano', symbol: 'ADA', logo: CRYPTO_LOGOS.ADA },
    { id: 'matic', name: 'Polygon', symbol: 'MATIC', logo: CRYPTO_LOGOS.MATIC },
    { id: 'dot', name: 'Polkadot', symbol: 'DOT', logo: CRYPTO_LOGOS.DOT },
    { id: 'avax', name: 'Avalanche', symbol: 'AVAX', logo: CRYPTO_LOGOS.AVAX },
    { id: 'shib', name: 'Shiba Inu', symbol: 'SHIB', logo: CRYPTO_LOGOS.SHIB },
    { id: 'trx', name: 'TRON', symbol: 'TRX', logo: CRYPTO_LOGOS.TRX },
  ]
};

const FAQ_ITEMS = [
  {
    question: "Comment effectuer un échange sur notre service ?",
    answer: `Toutes les transactions sur alphaswapp sont effectuées automatiquement sous le contrôle de nos opérateurs.

1. Sélectionnez la direction de l'échange dans la colonne "Je donne" et la colonne "Je reçois"
2. Remplissez tous les détails et cliquez sur le bouton "Procéder à l'échange"
3. Vérifiez l'exactitude de vos données et cliquez sur le bouton "Procéder au paiement". Vous devez effectuer le paiement dans les 17 minutes.
4. Après une opération d'échange réussie, n'hésitez pas à exprimer votre opinion sur le service alphaswapp.`
  },
  {
    question: "Combien de temps dure l'échange ?",
    answer: "Les échanges de devises électroniques sont instantanés. Le temps nécessaire au transfert des cryptomonnaies dépend de la vitesse des confirmations du réseau, généralement entre 5 et 30 minutes après l'envoi."
  },
  {
    question: "Pourquoi ma demande a-t-elle été rejetée ?",
    answer: `Une demande peut être rejetée dans les cas suivants :

1. Vous n'avez pas effectué de paiement dans les 17 minutes suivant la soumission de la demande
2. Vous avez transféré un montant incorrect
3. Vous avez spécifié des détails incorrects
4. Vous avez entré une adresse email invalide`
  }
];

const ExchangeWidget = ({ onExchangeSubmit }) => {
  const { t } = useLanguage();
  const [mode, setMode] = useState('sell');
  const [rates, setRates] = useState({});
  const [rateTimer, setRateTimer] = useState(30);

  const [giveAmount, setGiveAmount] = useState('');
  const [receiveAmount, setReceiveAmount] = useState('');
  const [selectedGiveMethod, setSelectedGiveMethod] = useState(PAYMENT_METHODS.crypto[0]);
  const [selectedReceiveMethod, setSelectedReceiveMethod] = useState(PAYMENT_METHODS.fiat[0]);
  const [giveAccountInfo, setGiveAccountInfo] = useState('');
  const [receiveAccountInfo, setReceiveAccountInfo] = useState('');
  const [email, setEmail] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeAML, setAgreeAML] = useState(false);
  const [subscribeNews, setSubscribeNews] = useState(false);

  const [rateLock, setRateLock] = useState(null);
  const [lockRemaining, setLockRemaining] = useState(0);
  const rateLockRef = useRef(null);
  const [platform, setPlatform] = useState({ exchange_enabled: true, closed_message: '' });

  const [openFAQ, setOpenFAQ] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showGiveDropdown, setShowGiveDropdown] = useState(false);
  const [showReceiveDropdown, setShowReceiveDropdown] = useState(false);

  useEffect(() => {
    fetchRates();
    api.get('/settings/public').then((r) => setPlatform(r.data)).catch(() => {});
    const rateInterval = setInterval(fetchRates, 10000);
    const timerInterval = setInterval(() => {
      setRateTimer(prev => prev <= 1 ? 30 : prev - 1);
      if (rateLockRef.current) {
        const expires = new Date(rateLockRef.current.expires_at).getTime();
        const remaining = Math.max(0, Math.floor((expires - Date.now()) / 1000));
        setLockRemaining(remaining);
        if (remaining <= 0) {
          rateLockRef.current = null;
          setRateLock(null);
        }
      }
    }, 1000);
    return () => { clearInterval(rateInterval); clearInterval(timerInterval); };
  }, []);

  useEffect(() => {
    if (mode === 'sell') {
      setSelectedGiveMethod(PAYMENT_METHODS.crypto[0]);
      setSelectedReceiveMethod(PAYMENT_METHODS.fiat[0]);
    } else {
      setSelectedGiveMethod(PAYMENT_METHODS.fiat[0]);
      setSelectedReceiveMethod(PAYMENT_METHODS.crypto[0]);
    }
    setGiveAmount('');
    setReceiveAmount('');
    setGiveAccountInfo('');
    setReceiveAccountInfo('');
    clearLock();
  }, [mode]);

  useEffect(() => {
    calculateReceiveAmount();
  }, [giveAmount, selectedGiveMethod, selectedReceiveMethod, rates, rateLock]);

  const clearLock = () => { rateLockRef.current = null; setRateLock(null); setLockRemaining(0); };

  const fetchRates = async () => {
    try {
      const response = await cryptoApi.getRates();
      setRates(response.data);
      setRateTimer(30);
    } catch (error) {
      console.error('Error fetching rates:', error);
    }
  };

  const getCryptoSymbol = () => mode === 'sell' ? selectedGiveMethod.symbol : selectedReceiveMethod.symbol;

  const getEffectivePrice = () => {
    if (rateLock) return rateLock.rate;
    const cryptoSymbol = getCryptoSymbol();
    return rates[cryptoSymbol]?.eur || rates[cryptoSymbol]?.usd || 0;
  };

  const calculateReceiveAmount = () => {
    if (!giveAmount || parseFloat(giveAmount) <= 0) {
      setReceiveAmount('');
      return;
    }
    const amount = parseFloat(giveAmount);
    const cryptoPrice = getEffectivePrice();
    let result = 0;
    if (mode === 'sell') {
      result = amount * cryptoPrice * 0.93;
    } else {
      result = cryptoPrice > 0 ? (amount * 0.93) / cryptoPrice : 0;
    }
    setReceiveAmount(result.toFixed(mode === 'sell' ? 2 : 8));
  };

  const lockCurrentRate = async () => {
    if (!giveAmount || parseFloat(giveAmount) <= 0) {
      toast.error('Veuillez entrer un montant avant de bloquer le taux');
      return null;
    }
    const cryptoSymbol = getCryptoSymbol();
    try {
      const res = await cryptoApi.lockRate({
        from_currency: mode === 'sell' ? cryptoSymbol : 'EUR',
        to_currency: mode === 'sell' ? 'EUR' : cryptoSymbol,
        amount: parseFloat(giveAmount),
      });
      const lock = res.data;
      const stored = { ...lock, rate: rates[cryptoSymbol]?.eur || rates[cryptoSymbol]?.usd || lock.rate };
      rateLockRef.current = stored;
      setRateLock(stored);
      setLockRemaining(lock.remaining_seconds || 600);
      toast.success(t.common?.rateLockInfo || 'Taux garanti pendant 10 minutes');
      return stored;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de bloquer le taux');
      return null;
    }
  };

  const switchDirection = () => {
    const tempGiveMethod = selectedGiveMethod;
    setSelectedGiveMethod(selectedReceiveMethod);
    setSelectedReceiveMethod(tempGiveMethod);
    setGiveAmount(receiveAmount);
    setMode(mode === 'sell' ? 'buy' : 'sell');
  };

  const getPlaceholder = (method) => {
    if (method.type === 'bank') return 'IBAN ou numéro de compte';
    if (method.type === 'card') return '4890 4945 9385 0217';
    if (method.type === 'transfer') return 'FRXX XXXX XXXX XXXX';
    if (method.symbol) return 'Adresse du portefeuille';
    return 'Entrez les détails';
  };

  const getCurrentRate = () => {
    const cryptoSymbol = getCryptoSymbol();
    const price = getEffectivePrice();
    return `1 ${cryptoSymbol} = ${price.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} EUR`;
  };

  const formatLockTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    if (!giveAmount || parseFloat(giveAmount) <= 0) { toast.error('Veuillez entrer un montant valide'); return; }
    if (!receiveAccountInfo) { toast.error('Veuillez entrer les informations de réception'); return; }
    if (!email || !email.includes('@')) { toast.error('Veuillez entrer une adresse email valide'); return; }
    if (!agreeTerms) { toast.error('Veuillez accepter les Termes du Service'); return; }
    if (!agreeAML) { toast.error('Veuillez accepter la politique AML / CTF & KYC'); return; }

    setLoading(true);
    try {
      let lock = rateLockRef.current;
      if (!lock) {
        lock = await lockCurrentRate();
      }
      const exchangeData = {
        mode,
        give_amount: parseFloat(giveAmount),
        receive_amount: parseFloat(receiveAmount),
        give_method: selectedGiveMethod,
        receive_method: selectedReceiveMethod,
        give_account_info: giveAccountInfo,
        receive_account_info: receiveAccountInfo,
        email,
        subscribe_news: subscribeNews,
        rate_lock_id: lock?.id || null,
        rate_lock_expires_at: lock?.expires_at || null,
      };
      if (onExchangeSubmit) {
        await onExchangeSubmit(exchangeData);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setLoading(false);
    }
  };

  const giveOptions = mode === 'sell' ? PAYMENT_METHODS.crypto : PAYMENT_METHODS.fiat;
  const receiveOptions = mode === 'sell' ? PAYMENT_METHODS.fiat : PAYMENT_METHODS.crypto;

  const renderMethodIcon = (method) => {
    if (method.logo) {
      return (
        <img src={method.logo} alt={method.name} className="w-6 h-6 rounded-full object-contain"
          onError={(e) => { e.target.style.display = 'none'; }} />
      );
    }
    return (
      <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
        style={{ backgroundColor: method.color || '#6B7280' }}>
        {method.name.substring(0, 2)}
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="exchange-widget">
      {/* Left Column - Exchange Form */}
      <div className="bg-white rounded-3xl p-6 md:p-8 shadow-[0_12px_40px_rgb(0,0,0,0.06)]" data-testid="exchange-form">
        {/* Buy/Sell Toggle */}
        <div className="flex mb-6 bg-[#F5F6F7] rounded-full p-1" data-testid="mode-toggle">
          <button onClick={() => setMode('buy')}
            className={`flex-1 py-3 rounded-full font-bold transition-colors ${mode === 'buy' ? 'bg-[#191C1F] text-white' : 'text-gray-500 hover:text-gray-900'}`}
            data-testid="buy-btn">
            {t.exchangeWidget?.buy || 'Acheter'}
          </button>
          <button onClick={() => setMode('sell')}
            className={`flex-1 py-3 rounded-full font-bold transition-colors ${mode === 'sell' ? 'bg-[#191C1F] text-white' : 'text-gray-500 hover:text-gray-900'}`}
            data-testid="sell-btn">
            {t.exchangeWidget?.sell || 'Vendre'}
          </button>
        </div>

        {/* I Give Section */}
        <div className="mb-4" data-testid="give-section">
          <label className="text-sm text-gray-500 mb-2 block font-semibold">{t.exchangeWidget?.youGive || 'Je donne'}</label>
          <div className="bg-[#F5F6F7] rounded-2xl p-4">
            <div className="flex items-center space-x-3 mb-3">
              <input type="number" value={giveAmount} onChange={(e) => setGiveAmount(e.target.value)}
                placeholder="0.00"
                className="flex-1 bg-transparent text-2xl font-bold outline-none text-gray-900 placeholder-gray-300 font-mono-data w-full min-w-0"
                data-testid="give-amount-input" />
              <div className="relative">
                <button onClick={() => setShowGiveDropdown(!showGiveDropdown)}
                  className="flex items-center space-x-2 bg-white px-4 py-2.5 rounded-full border border-gray-200 hover:border-gray-400 transition-colors min-w-[140px]"
                  data-testid="give-method-btn">
                  {renderMethodIcon(selectedGiveMethod)}
                  <span className="font-bold text-gray-900 text-sm">{selectedGiveMethod.symbol || selectedGiveMethod.name}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
                {showGiveDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-100 rounded-2xl z-50 max-h-80 overflow-y-auto shadow-[0_16px_48px_rgb(0,0,0,0.12)]">
                    {giveOptions.map((option) => (
                      <button key={option.id}
                        onClick={() => { setSelectedGiveMethod(option); setShowGiveDropdown(false); clearLock(); }}
                        className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                        data-testid={`give-option-${option.id}`}>
                        {renderMethodIcon(option)}
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">{option.symbol || option.name}</div>
                          {option.symbol && <div className="text-xs text-gray-400">{option.name}</div>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <input type="text" value={giveAccountInfo} onChange={(e) => setGiveAccountInfo(e.target.value)}
              placeholder={getPlaceholder(selectedGiveMethod)}
              className="w-full bg-white text-sm text-gray-700 px-4 py-3 rounded-xl outline-none border border-gray-200 placeholder-gray-400"
              data-testid="give-account-input" />
          </div>
        </div>

        {/* Switch Button */}
        <div className="flex justify-center -my-2 relative z-10">
          <button onClick={switchDirection}
            className="bg-[#191C1F] p-3 rounded-full transition-transform hover:rotate-180 duration-300"
            data-testid="switch-btn">
            <ArrowUpDown className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* I Receive Section */}
        <div className="mb-4 mt-4" data-testid="receive-section">
          <label className="text-sm text-gray-500 mb-2 block font-semibold">{t.exchangeWidget?.youReceive || 'Je reçois'}</label>
          <div className="bg-[#F5F6F7] rounded-2xl p-4">
            <div className="flex items-center space-x-3 mb-3">
              <input type="text" value={receiveAmount} readOnly placeholder="0.00"
                className="flex-1 bg-transparent text-2xl font-bold outline-none text-gray-900 placeholder-gray-300 font-mono-data w-full min-w-0"
                data-testid="receive-amount-input" />
              <div className="relative">
                <button onClick={() => setShowReceiveDropdown(!showReceiveDropdown)}
                  className="flex items-center space-x-2 bg-white px-4 py-2.5 rounded-full border border-gray-200 hover:border-gray-400 transition-colors min-w-[140px]"
                  data-testid="receive-method-btn">
                  {renderMethodIcon(selectedReceiveMethod)}
                  <span className="font-bold text-gray-900 text-sm">{selectedReceiveMethod.symbol || selectedReceiveMethod.name}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
                {showReceiveDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-100 rounded-2xl z-50 max-h-80 overflow-y-auto shadow-[0_16px_48px_rgb(0,0,0,0.12)]">
                    {receiveOptions.map((option) => (
                      <button key={option.id}
                        onClick={() => { setSelectedReceiveMethod(option); setShowReceiveDropdown(false); clearLock(); }}
                        className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                        data-testid={`receive-option-${option.id}`}>
                        {renderMethodIcon(option)}
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">{option.symbol || option.name}</div>
                          {option.symbol && <div className="text-xs text-gray-400">{option.name}</div>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <input type="text" value={receiveAccountInfo} onChange={(e) => setReceiveAccountInfo(e.target.value)}
              placeholder={getPlaceholder(selectedReceiveMethod)}
              className="w-full bg-white text-sm text-gray-700 px-4 py-3 rounded-xl outline-none border border-gray-200 placeholder-gray-400"
              data-testid="receive-account-input" />
          </div>
        </div>

        {/* Rate Section + Rate Lock */}
        <div className={`rounded-2xl p-4 mb-4 transition-colors ${rateLock ? 'bg-emerald-50 border border-emerald-200' : 'bg-[#F5F6F7]'}`} data-testid="rate-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center space-x-2">
              <span className="text-gray-500 text-sm font-semibold">{t.exchangeWidget?.rate || 'Taux'}</span>
              <Info className="w-4 h-4 text-gray-300" />
            </div>
            <div className="flex items-center space-x-3">
              <span className="font-bold text-gray-900 font-mono-data text-sm" data-testid="current-rate">{getCurrentRate()}</span>
              {rateLock ? (
                <div className="flex items-center space-x-1.5 bg-emerald-500 text-white px-3 py-1.5 rounded-full" data-testid="rate-lock-badge">
                  <Lock className="w-3.5 h-3.5" />
                  <span className="text-sm font-bold font-mono-data">{formatLockTime(lockRemaining)}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1 bg-gray-200 px-2.5 py-1.5 rounded-full">
                  <Clock className="w-3.5 h-3.5 text-gray-600" />
                  <span className="text-sm font-semibold text-gray-600 font-mono-data">{rateTimer}s</span>
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              {rateLock ? (t.common?.rateLockInfo || 'Taux garanti pendant 10 minutes') : (t.common?.liveRates || 'Prix en temps réel')}
            </span>
            {!rateLock && (
              <button onClick={lockCurrentRate}
                className="flex items-center space-x-1.5 text-xs font-bold text-white bg-[#191C1F] hover:bg-[#33373B] px-3.5 py-2 rounded-full transition-colors"
                data-testid="lock-rate-btn">
                <Lock className="w-3.5 h-3.5" />
                <span>{t.common?.rateLocked || 'Bloquer ce taux'} · 10 min</span>
              </button>
            )}
          </div>
        </div>

        {/* Email Field */}
        <div className="mb-4" data-testid="email-section">
          <label className="text-sm text-gray-500 mb-2 block font-semibold">{t.exchangeWidget?.email || 'E-mail'}</label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300" />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="votre@email.com"
              className="w-full bg-[#F5F6F7] text-gray-900 pl-12 pr-4 py-3.5 rounded-2xl outline-none border border-transparent placeholder-gray-400"
              data-testid="email-input" />
          </div>
        </div>

        {/* Checkboxes */}
        <div className="space-y-3 mb-6" data-testid="agreements-section">
          <div className="flex items-start space-x-3">
            <button onClick={() => setAgreeTerms(!agreeTerms)} className="mt-0.5" data-testid="terms-checkbox">
              {agreeTerms ? <CheckSquare className="w-5 h-5 text-[#191C1F]" /> : <Square className="w-5 h-5 text-gray-300" />}
            </button>
            <span className="text-sm text-gray-500">
              J'accepte les <a href="/agreement" className="text-gray-900 font-semibold hover:underline">{t.exchangeWidget?.terms || 'Termes du Service'}</a>
            </span>
          </div>
          <div className="flex items-start space-x-3">
            <button onClick={() => setAgreeAML(!agreeAML)} className="mt-0.5" data-testid="aml-checkbox">
              {agreeAML ? <CheckSquare className="w-5 h-5 text-[#191C1F]" /> : <Square className="w-5 h-5 text-gray-300" />}
            </button>
            <span className="text-sm text-gray-500">
              J'accepte la politique <a href="/privacy" className="text-gray-900 font-semibold hover:underline">AML / CTF & KYC</a>
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={() => setSubscribeNews(!subscribeNews)} data-testid="news-checkbox">
              {subscribeNews ? <CheckSquare className="w-5 h-5 text-[#191C1F]" /> : <Square className="w-5 h-5 text-gray-300" />}
            </button>
            <span className="text-sm text-gray-400">S'abonner aux actualités et offres spéciales</span>
          </div>
        </div>

        {/* Exchange Button */}
        {!platform.exchange_enabled && (
          <div className="bg-gradient-to-r from-rose-500 to-orange-500 rounded-2xl p-4 mb-3 text-white text-sm font-semibold" data-testid="exchange-closed-banner">
            ⏸ {platform.closed_message || 'Les échanges sont temporairement suspendus. Merci de réessayer plus tard.'}
          </div>
        )}
        <button onClick={handleSubmit}
          disabled={loading || !giveAmount || !agreeTerms || !agreeAML || !platform.exchange_enabled}
          className="w-full btn-pill-dark py-4 font-bold text-lg disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="exchange-btn">
          {loading ? (t.common?.processing || 'Traitement...') : (t.exchangeWidget?.exchange || 'Échanger maintenant')}
        </button>
      </div>

      {/* Right Column - Info & FAQ */}
      <div className="space-y-6" data-testid="info-column">
        {/* VIP Promotion Block */}
        <div className="gradient-pink-violet rounded-3xl p-8 text-white relative overflow-hidden" data-testid="vip-promo">
          <div className="relative z-10">
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm mb-4">
              <span className="w-2 h-2 bg-emerald-300 rounded-full animate-pulse"></span>
              <span className="font-semibold">Programme VIP</span>
            </div>
            <h3 className="text-3xl font-extrabold mb-2 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Échangez plus souvent,<br />gagnez du cashback
            </h3>
            <p className="text-white/80 mb-5 text-sm">
              Commission unique de 7% et jusqu'à 0.5% de cashback selon votre niveau VIP.
            </p>
            <a href="/auth"><button className="btn-pill-white px-6 py-2.5 text-sm">En savoir plus</button></a>
          </div>
        </div>

        {/* Rate lock explainer */}
        <div className="bg-black rounded-3xl p-8 text-white" data-testid="rate-lock-explainer">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><Lock className="w-5 h-5" /></div>
            <h3 className="text-xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.common?.rateLocked || 'Taux bloqué'} 10 minutes</h3>
          </div>
          <p className="text-white/60 text-sm leading-relaxed">
            {t.common?.rateLockInfo || 'Bloquez le prix affiché pendant 10 minutes : le taux de votre transaction est garanti, même si le marché bouge.'}
          </p>
        </div>

        {/* FAQ Section */}
        <div className="space-y-3" data-testid="faq-section">
          <h3 className="text-lg font-extrabold text-gray-900 mb-4 tracking-tight" style={{ fontFamily: 'Manrope' }}>Questions fréquentes</h3>
          {FAQ_ITEMS.map((item, index) => (
            <div key={index} className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid={`faq-item-${index}`}>
              <button onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors">
                <span className="font-semibold text-gray-900 pr-4 text-sm">{item.question}</span>
                {openFAQ === index ? <ChevronUp className="w-5 h-5 text-gray-900 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-300 flex-shrink-0" />}
              </button>
              {openFAQ === index && (
                <div className="px-4 pb-4 text-gray-500 text-sm whitespace-pre-line leading-relaxed">{item.answer}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExchangeWidget;
